import { Model, ModelConstructor } from './types';

export class ModelScopeManager {
  private refCounts = new Map<string, number>();

  private constructor(
    private models: Map<string, Model> = new Map(),
    private modelsByType: Map<string, Model[]> = new Map(),
    private parentModelScope?: ModelScopeManager
  ) {}

  static create(parentModelScope?: ModelScopeManager): ModelScopeManager {
    return new ModelScopeManager(new Map(), new Map(), parentModelScope);
  }

  addModel<T extends Model>(model: T): T {
    const typeKey = model.constructor.name;
    const fullId = `${typeKey}:${model.id}`;
    const existing = this.models.get(fullId);

    if (existing !== undefined) {
      const count = (this.refCounts.get(fullId) ?? 1) + 1;
      this.refCounts.set(fullId, count);
      return existing as T;
    }

    if (!this.modelsByType.has(typeKey)) {
      this.modelsByType.set(typeKey, []);
    }

    const typeModels = this.modelsByType.get(typeKey)!;
    this.models.set(fullId, model);
    typeModels.push(model);
    this.refCounts.set(fullId, 1);
    return model;
  }

  addModels(models: Model[]): Model[] {
    return models.map((model) => this.addModel(model));
  }

  removeModel(modelConstructor: ModelConstructor, modelId: string): void {
    const fullId = `${modelConstructor.name}:${modelId}`;
    const model = this.models.get(fullId);
    if (model === undefined) {
      return;
    }

    const count = (this.refCounts.get(fullId) ?? 1) - 1;
    this.refCounts.set(fullId, count);

    if (count > 0) {
      return;
    }

    this.refCounts.delete(fullId);
    model.dispose?.();

    this.models.delete(fullId);
    const typeModels = this.modelsByType.get(model.constructor.name);
    if (typeModels) {
      const index = typeModels.findIndex((m) => m.id === modelId);
      if (index >= 0) {
        typeModels.splice(index, 1);
      }
    }
  }

  clearScope(): void {
    this.models.clear();
    this.modelsByType.clear();
    this.refCounts.clear();
  }

  isInScope(modelConstructor: ModelConstructor, id?: string): boolean {
    const model = this.getModelOfType(modelConstructor, id);
    return !!model;
  }

  countInScopeModelsOfType<T extends Model>(
    modelConstructor: ModelConstructor<T>
  ): number {
    const models = this.modelsByType.get(modelConstructor.name);
    return (
      (models?.length ?? 0) +
      (this.parentModelScope?.countInScopeModelsOfType(modelConstructor) ?? 0)
    );
  }

  getAllInScopeModels(): Model[] {
    return Array.from(this.models.values());
  }

  /**
   * Get all models of a given type in the scope (including parent scopes).
   * Returns an empty array when none are present.
   */
  getInScopeModelsOfType<T extends Model>(
    modelConstructor: ModelConstructor<T>
  ): T[] {
    return this.getModelsOfType(modelConstructor);
  }

  getModelOfType<T extends Model>(
    modelConstructor: ModelConstructor<T>,
    id?: string
  ): T | null {
    if (id) {
      const fullId = `${modelConstructor.name}:${id}`;
      const model = this.models.get(fullId);
      return model && model.constructor.name === modelConstructor.name
        ? (model as T)
        : this.parentModelScope?.getModelOfType<T>(modelConstructor, id) ??
            null;
    }

    // Return the first model of this type if no ID specified
    const models = this.modelsByType.get(modelConstructor.name);
    return models && models.length > 0
      ? (models[0] as T)
      : this.parentModelScope?.getModelOfType<T>(modelConstructor) ?? null;
  }

  protected getModelsOfType<T extends Model>(
    modelConstructor: ModelConstructor<T>
  ): T[] {
    const models = this.modelsByType.get(modelConstructor.name) ?? [];
    return [
      ...(models as T[]),
      ...(this.parentModelScope?.getModelsOfType<T>(modelConstructor) ?? []),
    ];
  }
}
